/**
 * Live candle aggregation — OpenAlgo CandleBuilder pattern adapted for Bharat Charts.
 *
 * WS delivers LTP (+ optional cumulative day volume), not interval candles.
 * This builder buckets ticks into session-aligned OHLC so every timeframe
 * forms the same way: same tick → same bucket math → consistent candles.
 *
 * Pure/deterministic (bucket via floorBarTimeMs); no Date.now inside onTick —
 * callers pass tickTimeMs.
 */
import type { BcBar } from "./globals";
import { floorBarTimeMs, resMinutes } from "./session";

export type VolumeMode = "ltq-sum" | "day-delta" | "none";
export type LateTickPolicy = "foldIntoBar" | "drop";

export interface CandleBuilderOptions {
  resolution: string;
  symbol: string;
  volumeMode?: VolumeMode;
  lateTickPolicy?: LateTickPolicy;
  /** When opening a new bucket, seed open from previous close (TV/Dhan continuity). Default on. */
  openFromPrevClose?: boolean;
  /**
   * If the new bucket is more than this many steps ahead of the seeded bar,
   * refuse to invent — caller should tip-reconcile first (orphan-gap guard).
   */
  maxJumpBuckets?: number;
}

export interface LiveTick {
  /** Exchange or browser time in ms. */
  timeMs: number;
  price: number;
  /** Last-traded qty (if feed provides). */
  ltq?: number;
  /** Cumulative day volume (quote.volume). */
  cumDayVolume?: number;
}

export interface CandleUpdate {
  bar: BcBar;
  isNew: boolean;
  /** Tip edge too far behind — do not paint; reconcile first. */
  needsReconcile?: boolean;
}

export class CandleBuilder {
  private readonly resolution: string;
  private readonly symbol: string;
  private readonly volumeMode: VolumeMode;
  private readonly lateTickPolicy: LateTickPolicy;
  private readonly openFromPrevClose: boolean;
  private readonly maxJumpBuckets: number;
  private _current: BcBar | null = null;
  private _cumAtBarStart = 0;
  private _lastCum = 0;
  private _hasCum = false;

  constructor(opts: CandleBuilderOptions) {
    this.resolution = String(opts.resolution || "5");
    this.symbol = String(opts.symbol || "");
    this.volumeMode = opts.volumeMode || "day-delta";
    this.lateTickPolicy = opts.lateTickPolicy || "foldIntoBar";
    // Default true: mid-session open = prev.close (Dhan/TV continuity, all symbols).
    // Overnight gaps stay real via hist stitch skipping session-open bars.
    this.openFromPrevClose = opts.openFromPrevClose !== false;
    this.maxJumpBuckets = opts.maxJumpBuckets != null ? opts.maxJumpBuckets : 1.5;
  }

  /** Seed with last historical (closed or forming) bar so the first tick continues it. */
  seed(lastBar: BcBar | null | undefined, cumDayVolumeSoFar?: number): void {
    if (!lastBar || !(Number(lastBar.time) > 0) || !(Number(lastBar.close) > 0)) {
      this._current = null;
      return;
    }
    this._current = {
      time: Number(lastBar.time),
      open: Number(lastBar.open) || Number(lastBar.close),
      high: Number(lastBar.high) || Number(lastBar.close),
      low: Number(lastBar.low) || Number(lastBar.close),
      close: Number(lastBar.close),
      volume: Number(lastBar.volume) || 0,
    };
    if (cumDayVolumeSoFar != null && cumDayVolumeSoFar >= 0) {
      this._lastCum = cumDayVolumeSoFar;
      this._cumAtBarStart = Math.max(0, cumDayVolumeSoFar - (this._current.volume || 0));
      this._hasCum = true;
    }
  }

  current(): BcBar | null {
    return this._current ? { ...this._current } : null;
  }

  stepMs(): number {
    return resMinutes(this.resolution) * 60 * 1000;
  }

  bucketStart(timeMs: number): number {
    return floorBarTimeMs(timeMs, this.resolution, this.symbol);
  }

  /**
   * Feed one tick. Returns the affected bar, or null if dropped.
   * When needsReconcile is set, bar may still be the current (unmoved) seed —
   * caller must tip-heal before appending a far-ahead open bucket.
   */
  onTick(tick: LiveTick): CandleUpdate | null {
    const price = Number(tick.price);
    if (!(price > 0)) return null;
    const timeMs = Number(tick.timeMs) > 0 ? Number(tick.timeMs) : Date.now();
    const bs = this.bucketStart(timeMs);
    const step = this.stepMs();

    if (this._current !== null && bs < this._current.time) {
      if (this.lateTickPolicy === "drop") return null;
      this._foldInto(this._current, tick);
      return { bar: { ...this._current }, isNew: false };
    }

    if (this._current === null || bs > this._current.time) {
      if (this._current && step > 0) {
        const jump = (bs - this._current.time) / step;
        if (jump > this.maxJumpBuckets) {
          return {
            bar: { ...this._current },
            isNew: false,
            needsReconcile: true,
          };
        }
      }
      const open =
        this.openFromPrevClose && this._current && Number(this._current.close) > 0
          ? Number(this._current.close)
          : price;
      const vol = this._volumeForNewBar(tick);
      const bar: BcBar = {
        time: bs,
        open,
        high: Math.max(open, price),
        low: Math.min(open, price),
        close: price,
        volume: vol,
      };
      this._current = bar;
      return { bar: { ...bar }, isNew: true };
    }

    this._foldInto(this._current, tick);
    return { bar: { ...this._current }, isNew: false };
  }

  private _foldInto(bar: BcBar, tick: LiveTick): void {
    const price = Number(tick.price);
    if (price > bar.high) bar.high = price;
    if (price < bar.low) bar.low = price;
    bar.close = price;
    bar.volume = this._volumeForSameBar(bar, tick);
  }

  private _volumeForNewBar(tick: LiveTick): number {
    if (this.volumeMode === "none") return 0;
    if (this.volumeMode === "ltq-sum") return tick.ltq ?? 0;
    const cum = tick.cumDayVolume ?? 0;
    if (!this._hasCum) {
      this._cumAtBarStart = cum;
    } else if (cum < this._lastCum) {
      this._cumAtBarStart = 0;
    } else {
      this._cumAtBarStart = this._lastCum;
    }
    this._lastCum = cum;
    this._hasCum = true;
    return Math.max(0, cum - this._cumAtBarStart);
  }

  private _volumeForSameBar(bar: BcBar, tick: LiveTick): number {
    if (this.volumeMode === "none") return Number(bar.volume) || 0;
    if (this.volumeMode === "ltq-sum") return (Number(bar.volume) || 0) + (tick.ltq ?? 0);
    const cum = tick.cumDayVolume ?? 0;
    this._lastCum = cum;
    return Math.max(0, cum - this._cumAtBarStart);
  }
}

/** All intraday TFs we keep in lockstep from the 5m base tip. */
export const UNIVERSAL_INTRADAY_TFS = ["1", "3", "5", "10", "15", "30", "45", "60", "240"] as const;
